import useScript from "hooks/useScript";
import { BrowserRouter as Router, Switch } from "react-router-dom";
import { getEnvironment, loadAssetsCdnUrl } from "utils/env";
import { loadBrandParam, mapString } from "utils/common";
import { Routes } from "./components/shared/Routes";
import Header from "./components/shared/Header";
import Footer from "./components/shared/Footer";
import Layout from "./components/layout/Layout";
import Modal from "components/shared/Modal";
import appConfig from "./config.json";
import { loadLocaleParam } from "utils/locale";
import { loadCustomThemes, extractThemeFromBodyClasses } from "utils/theme";
import Decoration from "./components/layout/Decoration";
import { ExternalScripts } from "utils/externalScripts";

const mainAppPaths = ["/help", "/faq"];

const {
	routes,
	environments,
	cdn,
	brands,
	navigation,
	defaultImageDecoration,
} = appConfig;
const mainClass = "help-wrapper";
const env = getEnvironment();
const config = environments[env];
const brand = loadBrandParam();
const brandConfig = brands?.[brand];
const cdnAssetsUrl = loadAssetsCdnUrl(`${cdn?.baseUrl}`, env);
const helpAssetsUrl = loadAssetsCdnUrl(
	`${cdn?.baseUrl}${cdn?.helpAssetsPath}`,
	env,
	true
);
const currentLocale = loadLocaleParam();
document.documentElement.lang = currentLocale;

const getNavigationParameters = (defaultQuery) => {
	const queryObject = { ...defaultQuery, ...brandConfig?.navigationParameters };
	let queryString = "";
	for (const key in queryObject) {
		queryString += `${key}=${queryObject[key]}&`;
	}
	return queryString.substring(0, queryString.length - 1);
};

const isMainApp = () => {
	const { pathname } = window.location;
	for (let index = 0; index < mainAppPaths.length; index++) {
		if (pathname.includes(mainAppPaths[index])) {
			return true;
		}
	}
	return false;
};

if (isMainApp()) {
	document.body.classList.add("help-and-faq-background");
}

const loadNavigation = () => {
	let headerUrl = "";
	let footerUrl = "";
	if (brandConfig?.syndicatedHeader && brandConfig?.syndicatedFooter) {
		const baseUrl = config.syndicatedBaseUrl || window.location.origin;
		headerUrl = `${baseUrl}${brandConfig.syndicatedHeader.url}&locale=${currentLocale}&language=${currentLocale}`;
		footerUrl = `${baseUrl}${brandConfig.syndicatedFooter.url}&locale=${currentLocale}&language=${currentLocale}`;
	} else {
		headerUrl = mapString(
			`${cdnAssetsUrl}${navigation.cdnUrl}?${getNavigationParameters(
				navigation.query
			)}&brand=${brand}`,
			env === "development" ? "latest" : env
		);
	}

	if (!isMainApp()) {
		headerUrl += "&additionalProfileInfo=true";
	}

	return { headerUrl, footerUrl };
};

const App = () => {
	let { headerUrl, footerUrl } = loadNavigation();
	if (
		brandConfig.userAgentToPreventLoadingNavigationApp &&
		window?.navigator?.userAgent
			?.toLowerCase()
			.includes(brandConfig.userAgentToPreventLoadingNavigationApp)
	) {
		headerUrl = "";
		footerUrl = "";
	}

	useScript(headerUrl, "head");
	useScript(footerUrl, "head");
	extractThemeFromBodyClasses(helpAssetsUrl, brand);
	loadCustomThemes(helpAssetsUrl, brand);

	return (
		<div className={`${mainClass}__main`}>
			<Header />
			<ExternalScripts />
			<div className={`${mainClass}__glow-container`}>
				<div className={`${mainClass}__glow`}></div>
				<Decoration
					style={
						defaultImageDecoration && {
							backgroundImage: `url(${helpAssetsUrl}/images/${defaultImageDecoration})`,
						}
					}
					brand={brand}
					mainClass={mainClass}
				/>
			</div>
			<Router>
				<div
					className={`${mainClass}__container ${mainClass}__container--${currentLocale}`}
				>
					<Switch>
						{routes.map((route, i) => (
							<Routes key={i} path={route.path} component={Layout} />
						))}
					</Switch>
					<Modal />
				</div>
			</Router>
			{!config?.brands?.[brand]?.hideChat &&
				!(brandConfig?.chatAllowedLocales?.indexOf(currentLocale) === -1) && (
					<div
						id="lpChatButton"
						className={`help-chat help-chat--${brand}`}
						tabIndex="0"
						role="button"
					></div>
				)}
			<Footer />
		</div>
	);
};

export default App;
